import React, {useState, useEffect, useRef} from 'react';
import {Link} from 'react-router-dom';
import {compose} from 'recompose';
import withSignout from '../../auth/hoc/withSignout';
import styled from 'styled-components';
import getGreetingTime from '../utils/getGreetingTime.js';
import Tooltip from 'rc-tooltip';
import ApprovalButton from '../components/ApprovalButton';
import NCRDialog from '../../shared/components/ncr-dialog';
import {NCRCOMPANYLIST_LOOKUP_QUERY} from '../../timesheet/queries.js';
import RequisitionDialog from '../../shared/components/requisition-dialog';
import ViewOpenReqsModal from '../../shared/components/requisition-dialog/ViewRequisition.js';
import {POContainer} from '../../purchaseOrder/containers/POContainer.jsx';
import ViewOpenPOsModal from '../../purchaseOrder/components/ViewOpenPOsModal.jsx';

const ENABLE_VIEW_OPEN_POS = false;

const SettingsContainer = ({
  isUserManager,
  username,
  company,
  location,
  currentUser
}) => {
  const {permissionsGroups, EmployeePermissions} = currentUser;
  const [menuOpen, setMenuOpen] = useState(false);
  const [isNcrDialogOpen, setIsNcrDialogOpen] = useState(false);
  const [isRequisitionDialogOpen, setIsRequisitionDialogOpen] = useState(false);
  const [isViewOpenReqsModalOpen, setIsViewOpenReqsModalOpen] = useState(false);
  const [isViewOpenPOsModalOpen, setIsViewOpenPOsModalOpen] = useState(false);
  const [isPODialogueOpen, setIsPODialogueOpen] = useState(false);
  const [selectedPOForReceipt, setSelectedPOForReceipt] = useState(null);
  const [poOpenSource, setPoOpenSource] = useState('receipts');
  const [companies, setCompanies] = useState([]);
  const [isPOReceiptAllowed, setIsPOReceiptAllowed] = useState(
    permissionsGroups != null && permissionsGroups.includes('REC')
  );
  const menuRef = useRef(null);

  const toggleMenu = () => setMenuOpen(!menuOpen);
  const closeMenu = () => setMenuOpen(false);

  const openNcrDialog = () => setIsNcrDialogOpen(true);
  const closeNcrDialog = () => setIsNcrDialogOpen(false);
  const openViewOpenReqsModal = () => {
    setIsViewOpenReqsModalOpen(true);
    closeMenu();
  };
  const closeViewOpenReqsModal = () => setIsViewOpenReqsModalOpen(false);
  const openViewOpenPOsModal = () => {
    setIsViewOpenPOsModalOpen(true);
    closeMenu();
  };
  const closeViewOpenPOsModal = () => setIsViewOpenPOsModalOpen(false);

  const handlePODialogDismiss = () => {
    const fromOpenList = poOpenSource === 'openPOList';
    setIsPODialogueOpen(false);
    setSelectedPOForReceipt(null);
    setPoOpenSource('receipts');
    if (fromOpenList) {
      setIsViewOpenPOsModalOpen(true);
    }
  };

  const openPODialogue = () => {
    setPoOpenSource('receipts');
    setSelectedPOForReceipt(null);
    setIsPODialogueOpen(true);
    closeMenu();
  };

  const handleOpenPOSelected = selectedPO => {
    setPoOpenSource('openPOList');
    setSelectedPOForReceipt(selectedPO);
    setIsViewOpenPOsModalOpen(false);
    setIsPODialogueOpen(true);
  };

  const openRequisitionDialog = () => {
    setIsRequisitionDialogOpen(true);
    closeMenu();
  };

  const closeRequisitionDialog = () => {
    setIsRequisitionDialogOpen(false);
  };

  const handleClickOutside = event => {
    if (menuRef.current && !menuRef.current.contains(event.target)) {
      setMenuOpen(false);
    }
  };

  useEffect(() => {
    if (!menuOpen) return undefined;
    const handleEscape = event => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [menuOpen]);

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const response = await fetch('/api/graphql', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            query: NCRCOMPANYLIST_LOOKUP_QUERY.loc.source.body,
            variables: {}
          })
        });

        if (!response.ok) {
          throw new Error('Companies fetch failed');
        }

        const data = await response.json();
        setCompanies(data.data.ncrCompanyList);
      } catch (error) {
        console.error('Error fetching companies:', error);
      }
    };

    fetchCompanies();
  }, [company]);

  return (
    <div className="pull-right">
      {menuOpen && <MenuBackdrop onClick={closeMenu} />}
      <Container className="global-nav" ref={menuRef}>
        <h4>
          Good {getGreetingTime()}, {username}!
        </h4>
        <MenuIcon onClick={toggleMenu}>
          <i
            className={`fa ${menuOpen ? 'fa-times' : 'fa-bars'}`}
            aria-hidden="true"></i>
        </MenuIcon>
        <Menu open={menuOpen} aria-hidden={!menuOpen}>
          <CloseButton
            type="button"
            aria-label="Close menu"
            onClick={closeMenu}>
            <i className="fa fa-times" aria-hidden="true" />
          </CloseButton>
          <MenuHeader className="menu-header">
            <Tooltip
              placement="left"
              overlay={<span>Quick Start Guide</span>}>
              {isUserManager ? (
                company === '100' ? (
                  <a href="/US-Supervisor.pdf" target="_blank">
                    Quick Start Guide
                  </a>
                ) : (
                  <a href="/UK-Supervisor.pdf" target="_blank">
                    Quick Start Guide
                  </a>
                )
              ) : company === '100' ? (
                <a href="/US-Non-Supervisor.pdf" target="_blank">
                  Quick Start Guide
                </a>
              ) : (
                <a href="/UK-Non-Supervisor.pdf" target="_blank">
                  Quick Start Guide
                </a>
              )}
            </Tooltip>
            <Tooltip placement="left" overlay={<span>Quality Issue Form</span>}>
              <Link
                to="/quality-issue-form/ncr"
                className="ncr-menu-item"
                onClick={closeMenu}>
                Quality Issue Form
              </Link>
            </Tooltip>

            {EmployeePermissions.allowReq && (
              <>
                <Tooltip
                  placement="left"
                  overlay={<span>Requisition Form</span>}>
                  <button
                    onClick={() => setIsRequisitionDialogOpen(true)}
                    className="ncr-menu-item">
                    Requisition Form
                  </button>
                </Tooltip>
                <Tooltip
                  placement="left"
                  overlay={<span>View Open Reqs</span>}>
                  <button
                    onClick={openViewOpenReqsModal}
                    className="ncr-menu-item">
                    View Open Reqs
                  </button>
                </Tooltip>
                {isPOReceiptAllowed && (
                  <Tooltip
                    placement="left"
                    overlay={<span>View Open Purchase Orders</span>}>
                    <button
                      onClick={openViewOpenPOsModal}
                      className="ncr-menu-item">
                      View Open Purchase Orders
                    </button>
                  </Tooltip>
                )}
              </>
            )}
            {isPOReceiptAllowed && (
              <Tooltip
                placement="left"
                overlay={<span>Receive for Active Purchase Orders</span>}>
                <button onClick={openPODialogue} className="ncr-menu-item">
                  Purchase Order Receipts
                </button>
              </Tooltip>
            )}
            <Tooltip placement="left" overlay={<span>Approvals</span>}>
              <span onClick={closeMenu}>
                <ApprovalButton location={location} />
              </span>
            </Tooltip>
            <Tooltip placement="left" overlay={<span>Log Out</span>}>
              <Link to="/logout" onClick={closeMenu}>
                Log Out
              </Link>
            </Tooltip>
          </MenuHeader>
        </Menu>

        {isNcrDialogOpen && (
          <NCRDialog
            heading="Non Conformance Entry."
            onClose={closeNcrDialog}
            title="Enter Non Conformance Information"
          />
        )}
        {isRequisitionDialogOpen && (
          <RequisitionDialog
            heading="Requisition"
            onClose={closeRequisitionDialog}
            title="Create Requisition"
            currentUser={currentUser}
          />
        )}
        {isViewOpenReqsModalOpen && (
          <ViewOpenReqsModal
            onClose={closeViewOpenReqsModal}
            company={company}
            currentUser={currentUser}
          />
        )}
        {isViewOpenPOsModalOpen && (
          <ViewOpenPOsModal
            onClose={closeViewOpenPOsModal}
            onSelectPO={handleOpenPOSelected}
          />
        )}
        {isPODialogueOpen && (
          <POContainer
            onClose={handlePODialogDismiss}
            onSave={handlePODialogDismiss}
            currentUser={currentUser}
            preselectedPO={selectedPOForReceipt}
          />
        )}
        {isRequisitionDialogOpen && (
          <RequisitionDialog
            heading="Requisition"
            onClose={closeRequisitionDialog}
            onSave={closeRequisitionDialog}
            selectedProject={null}
            title={`Create Requisition`}
            currentUser={currentUser}
          />
        )}
      </Container>
    </div>
  );
};

const Container = styled.div`
  position: relative;
  h4 {
    margin-right: 15px;
    font-size: 20px;
    color: #333;
  }
`;

const MenuIcon = styled.div`
  font-size: 24px;
  cursor: pointer;
  margin-right: 15px;
  z-index: 2;
  i {
    color: #333;
  }
`;

const MenuContainer = styled.div`
  position: relative;
`;

const Menu = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  transform: ${({open}) => (open ? 'translateX(0)' : 'translateX(100%)')};
  height: 100%;
  width: 20%;
  max-width: 300px;
  background-color: white;
  box-shadow: 0px 8px 16px 0px rgba(0, 0, 0, 0.2);
  z-index: 1002;
  display: flex;
  flex-direction: column;
  transition: transform 0.2s ease-in-out;
  overflow-y: auto;

  @media (max-width: 991px) {
    width: 85%;
    max-width: 320px;
  }
`;

const MenuBackdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1001;
  display: none;

  @media (max-width: 991px) {
    display: block;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  width: 44px;
  height: 44px;
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 24px;
  color: #333;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  z-index: 1;

  &:hover {
    background-color: #f0f0f0;
  }
`;

const MenuHeader = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 50px;
  a,
  button {
    text-align: left;
    border: none;
    background: none;
    cursor: pointer;
    width: 100%;
    padding: 20px;
    &:hover {
      background-color: #ddd;
    }
  }
  a {
    margin-top: 10px;
    padding: 20px;
  }
`;

const container = compose(withSignout);

export default container(SettingsContainer);
